/**
 * Auth API Client
 *
 * Typed API client for authentication endpoints with automatic error handling.
 * Provides methods for magic link authentication flow.
 *
 * @module features/auth/api/authApiClient
 * @see SPEC-005.5-auth-login-ui Requirements 6.1-6.6
 */

import { ApiError, ApiErrorCode } from '@linkode/shared';
import { useAuthStore } from '../store/authStore';

// ============================================================================
// Constants
// ============================================================================

/** API base URL from environment variable */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api-staging.getlinkode.com');

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response from POST /auth/magic-link
 */
export interface MagicLinkResponse {
  /** Success message indicating email was sent */
  message: string;
}

/**
 * Response from POST /auth/verify
 */
export interface VerifyResponse {
  /** Bearer token for authenticated requests */
  sessionToken: string;
  /** Token expiration timestamp in milliseconds */
  expiresAt: number;
  /** User's unique identifier (UUID) */
  userId: string;
  /** User's email address */
  email: string;
}

// ============================================================================
// API Client Interface
// ============================================================================

/**
 * Auth API Client Interface
 *
 * Defines the contract for authentication API operations.
 *
 * @see Requirements 6.2
 */
export interface AuthApiClient {
  /**
   * Request a magic link to be sent to the specified email
   *
   * @param email - User's email address (will be sanitized)
   * @returns Promise resolving to success message
   * @throws ApiError on failure
   */
  requestMagicLink(email: string): Promise<MagicLinkResponse>;

  /**
   * Verify a magic link token and obtain session credentials
   *
   * @param token - Magic link token from URL
   * @returns Promise resolving to session credentials
   * @throws ApiError on failure
   */
  verifyToken(token: string): Promise<VerifyResponse>;

  /**
   * Logout the current user and invalidate session
   *
   * @returns Promise resolving when logout is complete
   */
  logout(): Promise<void>;
}

// ============================================================================
// API Client Implementation
// ============================================================================

/**
 * Auth API Client Implementation
 *
 * Handles all authentication-related API calls with:
 * - Automatic Authorization header injection
 * - Unified error handling
 * - UNAUTHORIZED auto-logout
 *
 * @see Requirements 6.3, 6.4, 6.5, 6.6
 */
class AuthApiClientImpl implements AuthApiClient {
  /**
   * Get Authorization header if session token exists
   *
   * @returns Headers object with Authorization if authenticated
   * @see Requirements 6.4
   */
  private getAuthHeader(): HeadersInit {
    const token = useAuthStore.getState().sessionToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Handle API response with unified error handling
   *
   * @param response - Fetch Response object
   * @returns Parsed JSON response
   * @throws ApiError on non-OK response
   * @see Requirements 6.3, 6.5
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let error: ApiError;

      try {
        error = (await response.json()) as ApiError;
      } catch {
        // If response is not valid JSON, create a generic error
        error = {
          code: ApiErrorCode.INTERNAL_ERROR,
          message: 'An unexpected error occurred',
          requestId: 'unknown',
        };
      }

      // Handle UNAUTHORIZED - trigger logout and let caller handle redirect
      // @see Requirements 6.5
      if (error.code === ApiErrorCode.UNAUTHORIZED) {
        // Clear auth state (don't call API logout to avoid infinite loop)
        // Only clear state, don't call logout() which would make another API call
        useAuthStore.setState({
          isAuthenticated: false,
          userId: null,
          email: null,
          sessionToken: null,
          expiresAt: null,
          error: null,
          isLoading: false,
        });
      }

      throw error;
    }

    return response.json();
  }

  /**
   * Request a magic link to be sent to the specified email
   *
   * @param email - User's email address
   * @returns Promise resolving to success message
   * @throws ApiError on failure (RATE_LIMITED, VALIDATION_ERROR, etc.)
   * @see Requirements 6.2
   */
  async requestMagicLink(email: string): Promise<MagicLinkResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/magic-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });

    return this.handleResponse<MagicLinkResponse>(response);
  }

  /**
   * Verify a magic link token and obtain session credentials
   *
   * @param token - Magic link token from URL
   * @returns Promise resolving to session credentials
   * @throws ApiError on failure (UNAUTHORIZED for invalid/expired token)
   * @see Requirements 6.2
   */
  async verifyToken(token: string): Promise<VerifyResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    return this.handleResponse<VerifyResponse>(response);
  }

  /**
   * Logout the current user and invalidate session
   *
   * This method is fire-and-forget - errors are ignored since
   * the local state should be cleared regardless of API success.
   *
   * @returns Promise resolving when logout is complete
   * @see Requirements 6.2
   */
  async logout(): Promise<void> {
    // Clear local state first (optimistic update)
    await useAuthStore.getState().logout();

    // If we had a token, the store's logout already called the API
    // This method exists for interface completeness and explicit logout calls
    // The actual API call is handled by authStore.logout()
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of Auth API Client
 *
 * @example
 * ```typescript
 * import { authApiClient } from '../api/authApiClient';
 *
 * // Request magic link
 * await authApiClient.requestMagicLink('user@example.com');
 *
 * // Verify token
 * const { sessionToken, expiresAt } = await authApiClient.verifyToken(token);
 *
 * // Logout
 * await authApiClient.logout();
 * ```
 */
export const authApiClient: AuthApiClient = new AuthApiClientImpl();
